import CurrentFacilityContext, {
	type CurrentFacilityContextValue,
} from '@UI/Providers/CurrentFacilityProvider/CurrentFacilityContext';
import * as React from 'react';

/**
 * Reserved for components mounted under `RequireFacility`, which only renders its children once
 * `currentFacility !== null` — so the two are equivalent, and a guarded screen never needs its own
 * `null` branch for the current facility. `FacilitySelector` (mounted in `AppHeader`, above the routes,
 * and rendering itself when there is no facility to pick), the Users administration screen
 * (`UI/Pages/Administration/Users/**`, reachable by a `USER_READ_ACCOUNT` holder who reads the whole
 * account and has no need for a current facility), providers mounted above the `Routes`, and the guard
 * itself must all keep `useCurrentFacility` instead, which tolerates a `null` current facility.
 */
function useStrictCurrentFacility(): CurrentFacilityContextValue & {
	readonly currentFacility: NonNullable<CurrentFacilityContextValue['currentFacility']>;
} {
	const context = React.useContext(CurrentFacilityContext);

	if (context === null) {
		throw new Error('useStrictCurrentFacility must be used within a CurrentFacilityProvider.');
	}

	if (context.currentFacility === null) {
		throw new Error('useStrictCurrentFacility must be used when a current facility is resolved.');
	}

	return { ...context, currentFacility: context.currentFacility };
}

export default useStrictCurrentFacility;
